'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFantasyTeamByShareToken, useVote, useMyVote, useTopTeams } from '../hooks/useFootball'
import { useT } from '../contexts/LocaleContext'
import { getPositionLabel } from '../components/football/pitchLayouts'
import { showToast } from '../components/ui/Toast'
import type { FantasyTeam, FantasyTeamPlayer } from '../types'

export default function FantasyTeamVotingPage() {
  const navigate = useNavigate()
  const t = useT()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { data: teamData, isLoading, error } = useFantasyTeamByShareToken(token || '')
  const vote = useVote()
  const { data: myVoteData } = useMyVote(teamData?.team?.id || 0)
  const { data: topTeamsData } = useTopTeams(teamData?.team?.match_id || 0, 5)

  const [selectedRating, setSelectedRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)

  const team = teamData?.team
  const players = (teamData as { team: FantasyTeam; players?: FantasyTeamPlayer[] })?.players || []
  const myVote = myVoteData?.vote
  const votingOpen = teamData?.match?.voting_open ?? false

  useEffect(() => {
    if (myVote) setSelectedRating(myVote.rating)
  }, [myVote])

  const handleVote = async (rating: number) => {
    if (!team) return
    if (!votingOpen) {
      showToast(t('dreamTeam.voting.toastPollClosed'), 'error')
      return
    }
    try {
      await vote.mutateAsync({ teamId: team.id, rating })
      showToast(t('dreamTeam.voting.toastVoteRecorded'), 'success')
      setSelectedRating(rating)
    } catch (err: any) {
      showToast(err.response?.data?.message || t('dreamTeam.voting.toastVoteError'), 'error')
    }
  }

  const votePositionLabel = (pos: string) => {
    if (pos === 'def' || pos === 'fwd') return t(`dreamTeam.positions.voteShort.${pos}`)
    return getPositionLabel(pos, t)
  }

  if (isLoading) {
    return (
      <div className="lucky-fb mini-app-container pb-20 min-h-screen flex items-center justify-center">
        <div className="lucky-fb__empty">{t('common.loading')}</div>
      </div>
    )
  }

  if (error || !team) {
    return (
      <div className="lucky-fb mini-app-container pb-20 min-h-screen">
        <div className="lucky-fb__inner">
          <div className="lucky-fb__card lucky-fb__empty">{t('fantasy.vote.notFound')}</div>
          <button type="button" className="lucky-fb__cta" onClick={() => navigate('/football')}>
            {t('fantasy.vote.back')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="lucky-fb mini-app-container pb-20 min-h-screen">
      <div className="lucky-fb__inner">
        <header className="lucky-fb__header">
          <button type="button" className="lucky-wallet__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            →
          </button>
          <div className="lucky-wallet__header-grow">
            <h1>{team.name}</h1>
            <p>
              {team.formation} · ★ {team.avg_rating.toFixed(1)} ({team.vote_count})
            </p>
          </div>
          <span className={`lucky-side__chip ${team.is_submitted ? 'is-ok' : ''}`}>
            {team.is_submitted ? t('dreamTeam.status.submittedSpaced') : t('dreamTeam.status.draft')}
          </span>
        </header>

        <section className="lucky-fb__card">
          <div className="lucky-side__players">
            {players
              .slice()
              .sort((a, b) => a.slot_number - b.slot_number)
              .map((tp: FantasyTeamPlayer) => (
                <div key={tp.id} className="lucky-side__player">
                  <img
                    src={tp.player?.photo_url || '/placeholder.png'}
                    alt={tp.player?.name || ''}
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder.png'
                    }}
                  />
                  <div>
                    <strong>{tp.player?.name}</strong>
                    <span>
                      {t('dreamTeam.voting.slotLine', {
                        position: votePositionLabel(tp.position),
                        slot: tp.slot_number,
                      })}
                      {tp.player?.team ? ` · ${tp.player.team}` : ''}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </section>

        <section className="lucky-fb__card">
          <h2 className="lucky-fb__section-title">{t('dreamTeam.voting.sectionTitle')}</h2>
          {myVote ? (
            <div className="lucky-side__stars-block">
              <p className="is-ok">{t('dreamTeam.voting.alreadyVoted')}</p>
              <div className="lucky-side__stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className={star <= myVote.rating ? 'is-on' : ''}>
                    ★
                  </span>
                ))}
              </div>
              <em>{t('dreamTeam.voting.yourRating', { rating: myVote.rating })}</em>
            </div>
          ) : votingOpen ? (
            <div className="lucky-side__stars lucky-side__stars--pick">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={selectedRating >= star || hoverRating >= star ? 'is-on' : ''}
                  onClick={() => handleVote(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  disabled={vote.isPending}
                >
                  ★
                </button>
              ))}
            </div>
          ) : (
            <div className="lucky-fb__empty">
              {t('dreamTeam.voting.pollClosed')}
              <span>{t('dreamTeam.voting.pollClosedHint')}</span>
            </div>
          )}
        </section>

        {topTeamsData?.teams && topTeamsData.teams.length > 0 && (
          <section className="lucky-fb__card">
            <h2 className="lucky-fb__section-title">{t('dreamTeam.voting.topTeamsTitle')}</h2>
            <div className="lucky-side__board">
              {topTeamsData.teams.slice(0, 5).map((topTeam, idx) => (
                <div key={topTeam.id} className="lucky-side__board-row">
                  <em>#{idx + 1}</em>
                  <div>
                    <strong>{topTeam.name}</strong>
                    <span>
                      {topTeam.formation} · ★ {topTeam.avg_rating.toFixed(1)} ({topTeam.vote_count})
                    </span>
                  </div>
                  {topTeam.id === team.id && (
                    <i className="lucky-side__chip is-ok">{t('dreamTeam.voting.yourTeamChip')}</i>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
